import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, minBy, sortBy } from 'lodash-es';
import { EventToolbox } from 'client/utils/event-toolbox';
import { PAGE_ASYNC_CONTENT_LOAD } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildPopularStylesPath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { PageModel } from 'client/data/models/page';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { getMpgUrl } from 'site-modules/shared/utils/core-link-constructor';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { Select } from 'site-modules/shared/components/select/select';
import { Link } from 'site-modules/shared/components/link/link';
import { InsightsCards } from './insights-cards/insights-cards';

import './ev-insights.scss';

function EvInsightsUI({
  className = null,
  headingClassName = 'heading-4 d-inline me-md-1',
  vehicle,
  styles = null,
  sharedTrimName = null,
  visitorLocation = null,
  setModelValue,
  isPluginHybrid = false,
  isUsed = false,
  isMobile = false,
  isInternational = false,
}) {
  const containerRef = useRef();
  const onStyleChange = useCallback(
    ({ trim }) => {
      setModelValue('sharedValues.trimName', PageModel, trim);
    },
    [setModelValue]
  );

  const hasStyles = !!styles;
  useEffect(() => {
    if (hasStyles) {
      EventToolbox.fireCustomEvent(PAGE_ASYNC_CONTENT_LOAD, { target: containerRef.current?.parentNode });
    }
  }, [hasStyles]);

  if (!styles) {
    return null;
  }

  const {
    make: { name: makeName, slug: makeSlug },
    model: { name: modelName, slug: modelSlug },
    year,
  } = vehicle;
  const currentStyle =
    (styles && styles.find(({ trim }) => trim === sharedTrimName)) || minBy(styles, 'price.baseMSRP');

  return (
    <ScrollElement
      id={MENU_HASH.EV_INSIGHTS}
      innerRef={containerRef}
      className={classnames('ev-insights bg-cool-gray-90 py-1_5 px-1 rounded-12', className)}
      data-tracking-parent="edm-entry-ev-insights"
    >
      <div className="mb-1">
        <i className="type-icon size-20 text-blue-40 icon-power2 me-0_5" aria-hidden />
        <h2 className={headingClassName}>
          {isUsed && year} {makeName} {modelName} {!modelName.includes('EV') && !isPluginHybrid && 'EV'}{' '}
          {isPluginHybrid && 'PHEV'} Insights
        </h2>
        <div className="d-md-inline-block mt-0_75 mt-md-0">
          Trim:
          <div className="d-inline-block pos-r ms-0_5">
            <div className="model-trim-label text-blue-40 pos-r bg-cool-gray-90">
              {modelName} {currentStyle.trim} <i className="icon-arrow-down4 size-10" aria-hidden />
            </div>
            <Select
              onChange={onStyleChange}
              valueKey="name"
              value={currentStyle.name}
              options={styles}
              name="ev-insights-trim-select"
              className="ev-insights-select text-truncate border-0 rounded h-100 w-100 pos-a left-0 top-0"
              labelText="Select style"
              data-tracking-id="style_select"
              isLabelHidden
            />
          </div>
        </div>
      </div>
      <InsightsCards
        styleId={currentStyle.id}
        vehicle={vehicle}
        className={className}
        visitorLocation={visitorLocation}
        isPluginHybrid={isPluginHybrid}
        isUsed={isUsed}
        isMobile={isMobile}
        isInternational={isInternational}
      />
      <Link
        to={getMpgUrl({ makeSlug, modelSlug, year, styleId: currentStyle.id })}
        className="text-primary-darker"
        data-tracking-id="mpg_model_select"
      >
        See All {isPluginHybrid ? 'PHEV' : 'EV'} Insights
        <i className="icon-arrow-right3 small ms-0_25" aria-hidden />
      </Link>
    </ScrollElement>
  );
}

EvInsightsUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  setModelValue: PropTypes.func.isRequired,
  styles: VehicleEntities.PopularStyles,
  sharedTrimName: PropTypes.string,
  className: PropTypes.string,
  headingClassName: PropTypes.string,
  visitorLocation: VisitorEntities.Location,
  isPluginHybrid: PropTypes.bool,
  isUsed: PropTypes.bool,
  isMobile: PropTypes.bool,
  isInternational: PropTypes.bool,
};

export const propsAreEqual = (prevProps, { styles }) => !styles;
export const EvInsights = connectToModel(React.memo(EvInsightsUI, propsAreEqual), {
  visitorLocation: bindToPath('location', VisitorModel),
  sharedTrimName: bindToPath('sharedValues.trimName', PageModel),
  styles: bindToPath(
    ({ vehicle }) => buildPopularStylesPath(getParamsFromVehicle(vehicle)),
    VehicleModel,
    popularStyles =>
      sortBy(
        popularStyles.map((style, index) => ({
          ...style,
          label: `${style.name}${index ? '' : ' (Most Popular)'} - ${getPriceString(
            get(style, 'price.baseMSRP')
          )} MSRP`,
        })),
        'price.baseMSRP'
      )
  ),
});
